import { useEffect, useMemo, useRef, useState } from "react";
import { RoomEvent, type MatrixClient, type Room } from "matrix-js-sdk";
import { Spinner, Text } from "../../theme/primitives";
import { groupByDay } from "./groupByDay";
import { MessageBubble } from "./MessageBubble";
import { DaySeparator } from "./DaySeparator";
import { useReactions } from "./useReactions";
import { useRoomMemberProfiles } from "./useRoomMemberProfiles";
import { isGroupRoom } from "../../matrix/rooms";
import type { ChatMessage } from "../../matrix/timeline";

/** Deterministic, readable-on-white label color from a user id. */
function senderColor(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) hash = input.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 40%)`;
}

interface MessageListProps {
  client: MatrixClient;
  room: Room;
  messages: ChatMessage[];
  loadingOlder: boolean;
  canLoadMore: boolean;
  onLoadOlder: () => void;
  otherUserId: string | null;
}

/**
 * Renders the timeline with day separators, auto-scrolls to the bottom on new
 * messages, and triggers scrollback when scrolled near the top.
 */
export function MessageList({
  client,
  room,
  messages,
  loadingOlder,
  canLoadMore,
  onLoadOlder,
  otherUserId,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const lastCountRef = useRef(0);

  const items = useMemo(() => groupByDay(messages), [messages]);
  const reactions = useReactions(client, room);
  const isGroup = useMemo(() => isGroupRoom(client, room), [client, room, messages.length]);
  const memberProfiles = useRoomMemberProfiles(client, room);

  // In group rooms, label each incoming message with the sender's name + company.
  const senderLabelFor = (m: ChatMessage): { name: string; color: string } | undefined => {
    if (!isGroup || m.isMine) return undefined;
    const profile = memberProfiles.get(m.sender);
    const name = profile?.displayName || room.getMember(m.sender)?.name || m.sender;
    const company = profile?.meta?.company?.value ?? profile?.company ?? undefined;
    const label = company ? `${name} · ${company}` : name;
    return { name: label, color: senderColor(m.sender) };
  };

  // Bump on every read-receipt for this room so the "read" state updates LIVE
  // (previously it only refreshed when a new message changed messages.length,
  // so ticks stayed grey until you sent another message).
  const [receiptTick, setReceiptTick] = useState(0);
  useEffect(() => {
    const onReceipt = (_event: unknown, eventRoom?: Room) => {
      if (eventRoom?.roomId === room.roomId) setReceiptTick((t) => t + 1);
    };
    client.on(RoomEvent.Receipt, onReceipt);
    return () => {
      client.removeListener(RoomEvent.Receipt, onReceipt);
    };
  }, [client, room.roomId]);

  // Which of MY messages the other user has read (receipt up-to).
  const readUpToTs = useMemo(() => {
    if (!otherUserId) return 0;
    const eventId = room.getEventReadUpTo(otherUserId);
    if (!eventId) return 0;
    const ev = room.findEventById(eventId);
    return ev?.getTs() ?? 0;
  }, [room, otherUserId, messages.length, receiptTick]);

  useEffect(() => {
    // Auto-scroll to bottom when messages are appended (not on scrollback).
    if (messages.length > lastCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: lastCountRef.current === 0 ? "auto" : "smooth" });
    }
    lastCountRef.current = messages.length;
  }, [messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 80 && canLoadMore && !loadingOlder) {
      onLoadOlder();
    }
  };

  if (messages.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-bg-chat)",
        }}
      >
        <Text muted>No messages yet. Say hello 👋</Text>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-chat)", padding: "var(--space-md) 0" }}
    >
      {loadingOlder && (
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-sm)" }}>
          <Spinner size={20} />
        </div>
      )}
      {items.map((item) =>
        item.type === "day" ? (
          <DaySeparator key={item.key} ts={item.ts} />
        ) : (
          <MessageBubble
            key={item.key}
            client={client}
            roomId={room.roomId}
            message={item.message}
            read={item.message.isMine && item.message.ts <= readUpToTs}
            reactions={reactions[item.message.id] ?? []}
            senderLabel={senderLabelFor(item.message)}
          />
        ),
      )}
      <div ref={bottomRef} />
    </div>
  );
}
